import { computed, h } from 'vue';

import { warn } from '@components/utils';

import { customRenderSlot, renderColumnTitle } from '../utils';
import { INTERNAL_COL_DEFINE } from '../constants';

import { EXPAND_COLUMN, SELECTION_COLUMN } from './selection';

import type { Ref, ComputedRef } from 'vue';
import type { IContextSlots } from '../context';
import type { NTable } from '../interface';

export interface IUseColumns<RecordType> {
  filledColumns: NTable.NColumn.TTransformColumns<RecordType>;
}

export function useColumns<RecordType>(contextSlots: Ref<IContextSlots>): IUseColumns<RecordType> {
  const filledColumns = (columns: NTable.NColumn.TColumns<RecordType>) => fillSlots(columns, contextSlots);

  return { filledColumns };
}

/**
 * Parse `columns` & `children` into `columns`.
 */
export function useParseColumns<RecordType>(
  {
    columns: baseColumns,
    expandable,
    expandedKeys,
    getRowKey,
    onTriggerExpand,
    expandIcon,
    rowExpandable,
    expandIconColumnIndex,
    expandRowByClick,
    expandColumnWidth,
    expandFixed
  }: {
    columns: Ref<NTable.NColumn.TColumns<RecordType>>;
    expandable: Ref<boolean>;
    expandedKeys: Ref<Set<NTable.TKey>>;
    getRowKey: Ref<NTable.NRow.TGetRowKey<RecordType>>;
    onTriggerExpand: NTable.TTriggerEventHandler<RecordType>;
    expandIcon: Ref<NTable.NExpand.TRenderExpandIcon<RecordType>>;
    rowExpandable: Ref<((record: RecordType) => boolean) | undefined>;
    expandIconColumnIndex?: Ref<number>;
    expandRowByClick: Ref<boolean | undefined>;
    expandColumnWidth: Ref<number | string | undefined>;
    expandFixed: Ref<NTable.TFixed | undefined>;
  },
  transformColumns: Ref<(columns: NTable.NColumn.TColumns<RecordType>) => NTable.NColumn.TColumns<RecordType>>
): {
  columns: ComputedRef<NTable.NColumn.TColumns<RecordType>>;
  flattenColumns: ComputedRef<readonly NTable.NColumn.IColumn<RecordType>[]>;
} {
  const withExpandColumns = computed<NTable.NColumn.TColumns<RecordType>>(() => {
    if (expandable.value) {
      let cloneColumns = baseColumns.value.slice();

      if (expandIconColumnIndex && expandIconColumnIndex.value >= 0) {
        warn(
          'CdsTable',
          '`expandIconColumnIndex` is deprecated. Please use `Table.EXPAND_COLUMN` in `columns` instead.'
        );
      }

      // >>> Insert expand column if not exist
      if (!cloneColumns?.includes(EXPAND_COLUMN)) {
        const expandColIndex = expandIconColumnIndex?.value || 0;
        if (expandColIndex >= 0) {
          cloneColumns?.splice(expandColIndex, 0, EXPAND_COLUMN);
        }
      }

      // >>> Deduplicate additional expand column
      if (cloneColumns.filter((column) => column === EXPAND_COLUMN).length > 1) {
        warn('CdsTable', 'There exist more than one `EXPAND_COLUMN` in `columns`.');
      }

      const expandColumnIndex = cloneColumns.indexOf(EXPAND_COLUMN);
      cloneColumns = cloneColumns.filter((column, index) => column !== EXPAND_COLUMN || index === expandColumnIndex);

      // ======================= Check if expand column need to fixed =======================
      const prevColumn = baseColumns?.value[expandColumnIndex];

      let fixedColumn: NTable.TFixed | undefined;
      if ((expandFixed.value === 'left' || expandFixed.value) && !expandIconColumnIndex?.value) {
        fixedColumn = 'left';
      } else if (
        (expandFixed.value === 'right' || expandFixed.value) &&
        expandIconColumnIndex?.value === baseColumns.value.length
      ) {
        fixedColumn = 'right';
      } else {
        fixedColumn = prevColumn ? prevColumn.fixed! : undefined;
      }

      const expandedKeysValue = expandedKeys.value;
      const rowExpandableValue = rowExpandable.value;
      const expandIconValue = expandIcon.value;
      const expandRowByClickValue = expandRowByClick.value;

      // ========================= Create expandable column =========================
      const expandColumn: NTable.NColumn.IColumn & Record<string, any> = {
        [INTERNAL_COL_DEFINE]: {
          class: 'cds-table__expand-icon-col',
          columnType: 'EXPAND_COLUMN'
        },
        title: '',
        fixed: fixedColumn,
        class: 'cds-table__row-expand-icon-cell',
        width: expandColumnWidth.value,
        customRender: ({ record, index }) => {
          const rowKey = getRowKey.value(record, index);
          const expanded = expandedKeysValue.has(rowKey);
          const recordExpandable = rowExpandableValue ? rowExpandableValue(record) : true;

          const icon = expandIconValue({
            expanded,
            expandable: recordExpandable,
            record,
            onExpand: onTriggerExpand
          });

          if (expandRowByClickValue) {
            return h('span', { onClick: (event: MouseEvent) => event.stopPropagation() }, icon);
          }

          return icon;
        }
      };

      return cloneColumns.map((col) => (col === EXPAND_COLUMN ? expandColumn : col));
    }

    if (baseColumns.value.includes(EXPAND_COLUMN)) {
      warn('CdsTable', '`expandable` is not config but there exist `EXPAND_COLUMN` in `columns`.');
    }

    return baseColumns.value.filter((col) => col !== EXPAND_COLUMN);
  });

  const mergedColumns = computed(() => {
    let finalColumn = withExpandColumns.value;

    if (transformColumns.value) {
      finalColumn = transformColumns.value(finalColumn);
    }

    if (!finalColumn.length) {
      finalColumn = [
        {
          customRender: () => null
        }
      ];
    }
    return finalColumn;
  });

  const flattenColumns = computed(() => {
    return flatColumns(mergedColumns.value);
  });

  // if (process.env.NODE_ENV !== 'production') {
  //   watchEffect(() => {
  //     setTimeout(() => {
  //       warningFixed(flattenColumns.value);
  //     });
  //   });
  // }

  return { columns: mergedColumns, flattenColumns };
}

export function useTitleColumns<RecordType>(
  columnTitleProps: Ref<NTable.NColumn.ITitleProps<RecordType>>
): IUseColumns<RecordType> {
  const filledColumns = (columns: NTable.NColumn.TColumns<RecordType>) => fillTitle(columns, columnTitleProps.value);

  return { filledColumns };
}

export function useColumnWidth(
  colWidthsRef: Ref<readonly (string | number | undefined)[]>,
  columnCountRef: Ref<number>
) {
  return computed(() => {
    const cloneColumns: number[] | string[] = [];
    const colWidths = colWidthsRef.value;
    const columnCount = columnCountRef.value;

    for (let i = 0; i < columnCount; i += 1) {
      const val = colWidths[i];
      if (val !== undefined) {
        cloneColumns[i] = val;
      } else {
        return null;
      }
    }

    return cloneColumns;
  });
}

function fillSlots<RecordType>(columns: NTable.NColumn.TColumns<RecordType>, contextSlots: Ref<IContextSlots>) {
  return columns.map((column) => {
    if (column === SELECTION_COLUMN || column === EXPAND_COLUMN) return column;
    const cloneColumn = { ...column };
    cloneColumn.__originColumn__ = column;

    if (contextSlots.value.headerCell) {
      cloneColumn.title = customRenderSlot(contextSlots.value, 'headerCell', { title: column.title, column }, () => [
        column.title as any
      ]);
    }

    if ('children' in cloneColumn && Array.isArray(cloneColumn.children)) {
      cloneColumn.children = fillSlots(cloneColumn.children, contextSlots);
    }

    return cloneColumn;
  });
}

function fillTitle<RecordType>(
  columns: NTable.NColumn.TColumns<RecordType>,
  columnTitleProps: NTable.NColumn.ITitleProps<RecordType>
) {
  return columns.map((column) => {
    const cloneColumn = { ...column };

    cloneColumn.title = renderColumnTitle(cloneColumn.title, columnTitleProps);

    if ('children' in cloneColumn) {
      cloneColumn.children = fillTitle(cloneColumn.children, columnTitleProps);
    }

    return cloneColumn;
  });
}

function flatColumns<RecordType>(columns: NTable.NColumn.TColumns<RecordType>): NTable.NColumn.IColumn<RecordType>[] {
  return columns.reduce((list, column) => {
    const { fixed } = column;

    // Convert `fixed='true'` to `fixed='left'` instead
    const parsedFixed = fixed === true ? 'left' : fixed;

    const subColumns = (column as NTable.NColumn.IColumnGroup<RecordType>).children;

    if (subColumns && subColumns.length > 0) {
      return [...list, ...flatColumns(subColumns).map((subColumn) => ({ fixed: parsedFixed, ...subColumn }))];
    }

    return [
      ...list,
      {
        ...column,
        fixed: parsedFixed
      }
    ] as any;
  }, []);
}

function warningFixed(flattenColumns: readonly { fixed?: NTable.TFixed }[]) {
  let allFixLeft = true;
  for (let i = 0; i < flattenColumns.length; i += 1) {
    const col = flattenColumns[i];
    if (allFixLeft && col.fixed !== 'left') {
      allFixLeft = false;
    } else if (!allFixLeft && col.fixed === 'left') {
      warn('CdsTable', `Index ${i - 1} of 'columns' missing 'fixed="left" prop.'`);
      break;
    }
  }

  let allFixRight = true;
  for (let i = flattenColumns.length - 1; i >= 0; i -= 1) {
    const col = flattenColumns[i];
    if (allFixRight && col.fixed !== 'right') {
      allFixRight = false;
    } else if (!allFixRight && col.fixed === 'right') {
      warn('CdsTable', `Index ${i + 1} of 'columns' missing 'fixed="right" prop.'`);
      break;
    }
  }
}
